import React, { useEffect, useRef } from 'react';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import theme from '../Room.module.scss';

interface RoomImageProps {
    roomUrl: string;
    roomConfig: {
        name: string;
    };
    showLoadingIndicator: boolean;
    onImageLoad: () => void;
    onImageError: () => void;
    /**
     * Cover-with-pan mode: enabled (by the caller) when the image is wider than the panel and so
     * cropped on its sides. Sizes the image to the panel's full height at its natural width, so it
     * overflows horizontally. The transform content then shrinks to the image (`fit-content`) instead
     * of filling the wrapper, which lets react-zoom-pan-pinch pan left/right within bounds — revealing
     * the cropped sides — while the full-height image stays locked on the y-axis. Off by default: the
     * image fills the panel and is only pannable once pinched/zoomed past scale 1.
     */
    coverPan?: boolean;
    /**
     * Zoom/pan enabled. Off for the placeholder image shown for rooms without a 3D visualization —
     * it's a stand-in, not a render of the current selection, so letting the user inspect it up close
     * would be misleading. When off, `coverPan` is irrelevant and ignored.
     */
    interactive?: boolean;
}

export const RoomImage: React.FC<RoomImageProps> = ({
    roomUrl,
    roomConfig,
    showLoadingIndicator,
    onImageLoad,
    onImageError,
    coverPan = false,
    interactive = true,
}) => {
    // When cover-pan turns on (or the image swaps while it's on), center the now-overflowing image so
    // it starts with an equal crop on both sides; the user pans from there. Instant (no animation).
    const transformRef = useRef<ReactZoomPanPinchRef>(null);
    useEffect(() => {
        // Only on activation — not on image swaps: the <img> element is reused (just its src changes),
        // so the library keeps the pan position, which is nicer when comparing options while panned.
        if (coverPan) transformRef.current?.centerView(1, 0);
    }, [coverPan]);

    // In cover-pan mode the wrapper is a plain box (no flex centering) so the content can overflow it
    // horizontally; the content shrinks to the image (`fit-content`) so react-zoom-pan-pinch measures
    // the overflow and pans within bounds. `centerView` above sets the initial centered position.
    const wrapperStyle: React.CSSProperties = coverPan
        ? { width: '100%', height: '100%' }
        : { width: '100%', height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' };
    const contentStyle: React.CSSProperties = coverPan
        ? { width: 'fit-content', height: '100%' }
        : { width: '100%', height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' };
    const imgStyle: React.CSSProperties = coverPan
        ? { opacity: showLoadingIndicator ? 0.5 : 1, height: '100%', width: 'auto', maxWidth: 'none', maxHeight: 'none' }
        : { opacity: showLoadingIndicator ? 0.5 : 1 };

    const loadingOverlay = showLoadingIndicator && (
        <div className={theme.loadingOverlay}>
            <LoadingIndicator />
        </div>
    );

    // Static mode: the plain <img>, no zoom/pan wrapper. Keeps the `.roomImage` class so the panel's
    // cover/contain rules (see `.visualPanelCover` in Room.module.scss) apply exactly as they do to a
    // real visualization — the layout is identical, only the interaction is gone.
    if (!interactive) {
        return (
            <>
                <div className={theme.staticImageHost}>
                    <img
                        className={theme.roomImage}
                        src={roomUrl}
                        alt={roomConfig.name}
                        onLoad={onImageLoad}
                        onError={onImageError}
                        style={{ opacity: showLoadingIndicator ? 0.5 : 1 }}
                    />
                </div>
                {loadingOverlay}
            </>
        );
    }

    return (
        <>
            <TransformWrapper
                ref={transformRef}
                centerOnInit
                maxScale={4}
                limitToBounds
                initialScale={1}
                minScale={1}
                // Cover-pan clamps hard to the image edges. `disablePadding` zeroes the drag over-pan
                // (the library otherwise lets you pull ~100px past the edge into empty space). Unlike
                // setting alignmentAnimation size to 0 — which also disables the pass that re-clamps
                // position after a zoom-out, stranding the image in a corner — this keeps alignment
                // working, so zooming back out snaps the image back to fully cover the panel.
                disablePadding={coverPan}>
                <TransformComponent wrapperStyle={wrapperStyle} contentStyle={contentStyle}>
                    <img
                        className={theme.roomImage}
                        src={roomUrl}
                        alt={roomConfig.name}
                        onLoad={onImageLoad}
                        onError={onImageError}
                        style={imgStyle}
                    />
                </TransformComponent>
            </TransformWrapper>

            {loadingOverlay}
        </>
    );
};
